import React, { useCallback, useRef, useState } from 'react'
import { MarkControlTestsCurrentRequest } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useConfirmationModal } from 'src/components/ModalForms/useConfirmationModal'
import { useConfirmationContext } from 'src/Confirmation/ConfirmationContext'
import { useInAudit } from 'src/context/AuditContext'
import { useAuditReadiness } from 'src/pages/Roadmap_v2/roadmap.hooks'
import { DerivedResizableTextArea } from 'src/components/Reusable/Inputs/TextInput'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useInvalidateAuditRunsCache } from './AuditBot.hooks'

const ATTESTATION =
  "These results were produced against an earlier version of this control's objective, guidance, policies, evidence or services. Marking them current records that you have confirmed they still reflect your environment today. The results themselves will not change or be re-examined. If anything changes from here, the results will be flagged as outdated again."

const MarkCurrentBody = ({
  reasonRef,
}: {
  reasonRef: React.MutableRefObject<string>
}): JSX.Element => {
  const [reason, setReason] = useState('')

  return (
    <FlexColumn align={FlexAlign.FLEX_START} gap={16} isFullWidth>
      <p>{ATTESTATION}</p>
      <DerivedResizableTextArea
        label="Reason / explanation (optional)"
        name="mark-current-explanation"
        initVal={reason}
        form="mark-current-explanation"
        maxInputLength={4000}
        customOnChange={(event) => {
          const value = (event.currentTarget as HTMLTextAreaElement).value
          setReason(value)
          reasonRef.current = value
        }}
      />
    </FlexColumn>
  )
}

// This is intentionally shared by bulk and single-result callers. The server
// remains the source of truth for stale/latest eligibility and for auditing.
export const useMarkControlTestsCurrent = (): ((
  ids: string[],
  onBack?: () => void,
) => void) => {
  const client = useAuthorizedGrpcClient(AuditBotPromiseClient)
  const invalidate = useInvalidateAuditRunsCache()
  const { auditId } = useInAudit()
  const { setConfirmationState } = useConfirmationContext()
  const selectedIDs = useRef<string[]>([])
  const reasonRef = useRef('')

  const confirm = useConfirmationModal({
    title: 'Mark Current',
    body: <MarkCurrentBody reasonRef={reasonRef} />,
    confirmText: 'Mark Current',
    throbber: { size: 's', color: 'primary' },
    onCancel: () => {
      reasonRef.current = ''
    },
    onConfirmCB: async () => {
      const request = new MarkControlTestsCurrentRequest()
        .setAuditControlTestIdsList(selectedIDs.current)
        .setAuditId(auditId || '')
        .setExplanation(reasonRef.current.trim())
      try {
        const response = await client.markControlTestsCurrent(request)
        if (response.getResultsList().some((result) => result.getError())) {
          showInfoToast(
            'Some of the selected results could not be marked current. Please try again.',
          )
        }
      } catch (error) {
        showInfoToast(
          'There was a problem marking the selected results current. Please try again.',
        )
      } finally {
        // The server marks each id independently, so a response carrying errors can
        // still carry successes. Refresh either way, or those keep rendering as
        // outdated and the retry reports them "not stale". The explanation belongs to
        // one submission and must never be inherited by the next dialog.
        reasonRef.current = ''
        await invalidate()
      }
    },
  })

  return useCallback(
    (ids: string[], onBack?: () => void) => {
      selectedIDs.current = Array.from(new Set(ids.filter(Boolean)))
      if (selectedIDs.current.length) {
        confirm()
        if (onBack) {
          setConfirmationState((state) => ({ ...state, onBack }))
        }
      }
    },
    [confirm, setConfirmationState],
  )
}

export const MarkControlTestCurrentButton = ({
  controlTestId,
  isStale,
}: {
  controlTestId: string
  isStale: boolean
}): JSX.Element | null => {
  const canMarkCurrent = useCanPerformAction(
    CAPABILITY.CAN_MARK_CONTROL_TESTS_CURRENT,
  )
  const markCurrent = useMarkControlTestsCurrent()

  if (!canMarkCurrent || !isStale) return null
  return (
    <TextButton
      style={{ whiteSpace: 'nowrap' }}
      onClick={(event) => {
        // The controls-index and requirement-tab rows wrap their content in a Link, so
        // stopping propagation alone still leaves the anchor's own navigation to fire.
        event.preventDefault()
        event.stopPropagation()
        markCurrent([controlTestId])
      }}
    >
      Mark Current
    </TextButton>
  )
}

export const useMarkStaleControlTestsCurrent = (): (() => void) => {
  const { auditId } = useInAudit()
  const { data } = useAuditReadiness({ auditId, calculateStaleness: true })
  const markCurrent = useMarkControlTestsCurrent()

  return useCallback(() => {
    const ids = (data?.getAuditReadinessList() || []).flatMap((record) =>
      record
        .getControlTestsList()
        .filter((test) => test.getIsStale())
        .map((test) => test.getId()),
    )
    markCurrent(ids)
  }, [data, markCurrent])
}
